import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material'
import {
  NotificationsActive,
  Email,
  Inbox,
  NightsStay,
  WbSunny,
  Speed,
} from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import { toast } from '../../store/toastStore'
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  type NotificationPreference,
} from '../../hooks/useNotificationPreferences'

const THROTTLE_OPTIONS = [
  { value: 0, label: 'notifPrefs.noThrottle' },
  { value: 60, label: 'notifPrefs.throttle1m' },
  { value: 300, label: 'notifPrefs.throttle5m' },
  { value: 900, label: 'notifPrefs.throttle15m' },
  { value: 3600, label: 'notifPrefs.throttle1h' },
]

export default function NotificationPreferencesPage() {
  const { t } = useTranslation()
  const { data: pref, isLoading, error } = useNotificationPreferences()
  const update = useUpdateNotificationPreferences()

  const [form, setForm] = useState({
    push_enabled: true,
    email_enabled: true,
    in_app_enabled: true,
    quiet_start: '',
    quiet_end: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    min_interval_seconds: 300,
  })

  const [dirty, setDirty] = useState(false)

  // Sync form when data loads.
  useEffect(() => {
    if (pref) {
      setForm({
        push_enabled: pref.push_enabled,
        email_enabled: pref.email_enabled,
        in_app_enabled: pref.in_app_enabled,
        quiet_start: pref.quiet_start ?? '',
        quiet_end: pref.quiet_end ?? '',
        timezone: pref.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        min_interval_seconds: pref.min_interval_seconds,
      })
    }
  }, [pref])

  const set = <K extends keyof typeof form>(key: K, val: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: val }))
    setDirty(true)
  }

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        push_enabled: form.push_enabled,
        email_enabled: form.email_enabled,
        in_app_enabled: form.in_app_enabled,
        quiet_start: form.quiet_start || null,
        quiet_end: form.quiet_end || null,
        timezone: form.timezone,
        min_interval_seconds: form.min_interval_seconds,
      })
      toast.success(t('notifPrefs.saved'))
      setDirty(false)
    } catch {
      // Error toast shown by API interceptor
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">{t('notifPrefs.loadError')}</Alert>
  }

  return (
    <Box>
      <PageHeader
        title={t('notifPrefs.title')}
        subtitle={t('notifPrefs.subtitle')}
        breadcrumbs={[
          { label: t('settings.title'), href: '/security' },
          { label: t('notifPrefs.title') },
        ]}
      />

      {/* Channels */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('notifPrefs.channels')}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={form.push_enabled}
                onChange={(e) => set('push_enabled', e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsActive fontSize="small" color="primary" />
                <Box>
                  <Typography variant="body1">{t('notifPrefs.push')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('notifPrefs.pushDesc')}
                  </Typography>
                </Box>
              </Box>
            }
            sx={{ display: 'flex', mb: 1, ml: 0 }}
          />

          <Divider sx={{ my: 1 }} />

          <FormControlLabel
            control={
              <Switch
                checked={form.email_enabled}
                onChange={(e) => set('email_enabled', e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email fontSize="small" color="primary" />
                <Box>
                  <Typography variant="body1">{t('notifPrefs.email')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('notifPrefs.emailDesc')}
                  </Typography>
                </Box>
              </Box>
            }
            sx={{ display: 'flex', mb: 1, ml: 0 }}
          />

          <Divider sx={{ my: 1 }} />

          <FormControlLabel
            control={
              <Switch
                checked={form.in_app_enabled}
                onChange={(e) => set('in_app_enabled', e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inbox fontSize="small" color="primary" />
                <Box>
                  <Typography variant="body1">{t('notifPrefs.inApp')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('notifPrefs.inAppDesc')}
                  </Typography>
                </Box>
              </Box>
            }
            sx={{ display: 'flex', ml: 0 }}
          />
        </CardContent>
      </Card>

      {/* Quiet hours */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('notifPrefs.quietHours')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('notifPrefs.quietHoursDesc')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label={t('notifPrefs.quietStart')}
              type="time"
              value={form.quiet_start}
              onChange={(e) => set('quiet_start', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
              InputProps={{
                startAdornment: <NightsStay fontSize="small" color="primary" sx={{ mr: 1 }} />,
              }}
            />
            <TextField
              label={t('notifPrefs.quietEnd')}
              type="time"
              value={form.quiet_end}
              onChange={(e) => set('quiet_end', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 160 }}
              InputProps={{
                startAdornment: <WbSunny fontSize="small" color="primary" sx={{ mr: 1 }} />,
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Throttle */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Speed fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('notifPrefs.throttle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('notifPrefs.throttleDesc')}
          </Typography>

          <TextField
            select
            label={t('notifPrefs.minInterval')}
            value={form.min_interval_seconds}
            onChange={(e) => set('min_interval_seconds', Number(e.target.value))}
            sx={{ minWidth: 200 }}
          >
            {THROTTLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.label)}
              </MenuItem>
            ))}
          </TextField>
        </CardContent>
      </Card>

      {/* Save */}
      <Button
        variant="contained"
        size="large"
        disabled={!dirty || update.isPending}
        onClick={handleSave}
      >
        {update.isPending ? <CircularProgress size={20} /> : t('notifPrefs.save')}
      </Button>

    </Box>
  )
}
